import React, { useState } from "react";
import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Wallet, ArrowDownCircle, ArrowUpCircle, ArrowLeftRight, Building2, Tags,
  Layers, Landmark, BarChart3, Users, ShieldCheck, Settings, ChevronLeft,
  Search, Bell, LogOut, Menu, ChevronDown, ScrollText, Target, Gauge, FileText,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { section: null, items: [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, perm: "dashboard.view", testId: "nav-dashboard-link" }] },
  {
    section: "Financeiro",
    items: [
      { to: "/contas-a-pagar", label: "Contas a Pagar", icon: ArrowDownCircle, perm: "payables.read", testId: "nav-contas-pagar-link" },
      { to: "/contas-a-receber", label: "Contas a Receber", icon: ArrowUpCircle, perm: "receivables.read", testId: "nav-contas-receber-link" },
      { to: "/transferencias", label: "Transferências", icon: ArrowLeftRight, perm: "financial_transfer.view", testId: "nav-transferencias-link" },
    ],
  },
  {
    section: "Cadastros",
    items: [
      { to: "/fornecedores", label: "Fornecedores", icon: Building2, perm: "suppliers.read", testId: "nav-fornecedores-link" },
      { to: "/categorias", label: "Categorias Financeiras", icon: Tags, perm: "categories.read", testId: "nav-categorias-link" },
      { to: "/centros-de-custo", label: "Centros de Custo", icon: Layers, perm: "cost_centers.read", testId: "nav-centros-custo-link" },
      { to: "/contas-financeiras", label: "Contas Financeiras", icon: Landmark, perm: "accounts.read", testId: "nav-contas-financeiras-link" },
    ],
  },
  {
    section: "Orçamento",
    items: [
      { to: "/orcamento", label: "Orçamento Anual", icon: Target, perm: "budget.view", testId: "nav-orcamento-link" },
      { to: "/orcado-x-realizado", label: "Orçado x Realizado", icon: Gauge, perm: "budget.view", testId: "nav-orcado-realizado-link" },
    ],
  },
  {
    section: "Relatórios",
    items: [
      { to: "/relatorios", label: "Relatórios", icon: BarChart3, perm: "reports.view", testId: "nav-relatorios-link" },
      { to: "/prestacao-de-contas", label: "Prestação de Contas", icon: FileText, perm: "statement.view", testId: "nav-prestacao-link" },
    ],
  },
  {
    section: "Configurações",
    items: [
      { to: "/usuarios", label: "Usuários", icon: Users, perm: "users.manage", testId: "nav-usuarios-link" },
      { to: "/perfis", label: "Perfis e Permissões", icon: ShieldCheck, perm: "profiles.manage", testId: "nav-perfis-link" },
      { to: "/auditoria", label: "Auditoria", icon: ScrollText, perm: "audit.view", testId: "nav-auditoria-link" },
    ],
  },
];

function Brand({ collapsed }) {
  return (
    <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10 shrink-0">
      <div className="h-9 w-9 rounded-lg bg-gold flex items-center justify-center text-brand-sidebar font-extrabold font-heading text-lg shadow">
        ML
      </div>
      {!collapsed && (
        <div className="leading-tight">
          <div className="text-white font-heading font-bold text-sm tracking-wide">MONTE LÍBANO</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-gold">Gestão Financeira</div>
        </div>
      )}
    </div>
  );
}

export default function AppLayout() {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "U").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  const SidebarInner = (
    <>
      <Brand collapsed={collapsed} />
      <nav className="flex-1 overflow-y-auto sidebar-scroll px-3 py-4 space-y-5">
        {NAV.map((group, gi) => {
          const items = group.items.filter((it) => hasPermission(it.perm));
          if (items.length === 0) return null;
          return (
            <div key={gi}>
              {group.section && !collapsed && (
                <div className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#5f7a6c]">
                  {group.section}
                </div>
              )}
              <div className="space-y-1">
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    data-testid={it.testId}
                    onClick={() => setMobileOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-brand text-white border-l-[3px] border-gold font-semibold"
                          : "text-[#A3B8AD] hover:bg-white/10 hover:text-white"
                      }`
                    }
                  >
                    <it.icon className="h-[18px] w-[18px] shrink-0" />
                    {!collapsed && <span className="truncate">{it.label}</span>}
                    {!collapsed && it.soon && (
                      <span className="ml-auto text-[9px] uppercase bg-white/10 text-gold px-1.5 py-0.5 rounded">Em breve</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar desktop */}
      <aside
        className={`hidden lg:flex flex-col fixed inset-y-0 left-0 z-40 bg-brand-sidebar transition-all duration-300 ${
          collapsed ? "w-20" : "w-64"
        }`}
        data-testid="sidebar"
      >
        {SidebarInner}
        <button
          onClick={() => setCollapsed((c) => !c)}
          data-testid="sidebar-toggle"
          className="h-11 flex items-center justify-center gap-2 border-t border-white/10 text-[#A3B8AD] hover:text-white text-xs transition-colors"
        >
          <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          {!collapsed && "Recolher"}
        </button>
      </aside>

      {/* Sidebar mobile */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-sidebar">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 flex flex-col bg-brand-sidebar">{SidebarInner}</aside>
        </div>
      )}

      {/* Conteúdo */}
      <div className={`transition-all duration-300 ${collapsed ? "lg:pl-20" : "lg:pl-64"}`}>
        <header className="h-16 sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200 px-4 sm:px-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-button">
              <Menu className="h-5 w-5" />
            </button>
            <div className="relative max-w-md w-full hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                data-testid="global-search-input"
                placeholder="Pesquisar..."
                className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/10 transition-all"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500" data-testid="notifications-button">
              <Bell className="h-5 w-5" />
              <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-gold" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 pl-2 pr-1 sm:pr-3 py-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu-button">
                  <div className="h-8 w-8 rounded-full bg-brand text-white flex items-center justify-center text-xs font-semibold">{initials}</div>
                  <div className="hidden sm:block text-left leading-tight">
                    <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
                    <div className="text-[11px] text-slate-400 capitalize">{(user?.profile_code || "").toLowerCase()}</div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user?.name}</div>
                  <div className="text-xs text-slate-400 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="logout-button" className="text-rose-600 focus:text-rose-600">
                  <LogOut className="h-4 w-4 mr-2" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6" data-testid="main-content" key={location.pathname}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
